<script lang="ts">
	import { Background } from "@components/Background.js";
	import BookMeeting from "@components/BookMeeting.svelte";
	import FaqItems from "@components/FaqItems.svelte";
	import FoldingCards from "@components/FoldingCards.svelte";
	import Section from "@components/Section.svelte";
	import ServicesCards from "@components/ServicesCards.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Link from "@components/common/Link.svelte";
	import { ButtonVariant } from "@components/common/getButtonClass.js";
	import Layout from "@components/layout/Layout.svelte";
	import arrowNext from "@core/assets/icons/arrow-next.svg?raw";
	import { getConnectWithFastestPath } from "@core/schema/paths/getConnectWithFastestPath.js";
	import { getWebAppsPath } from "@core/schema/paths/getWebAppsPath.js";
	import { BookMeetingPerson, getBookMeetingPeople } from "../data/getBookMeetingPeople.js";
	import { getFaqsSolutions } from "../data/getFaqsSolutions.js";
	import { getReasonsForCoop } from "../data/getReasonsForCoop.js";
	import { getSolutionsServices } from "../data/getSolutionsServices.js";
	import type { SolutionsProps } from "./SolutionsProps.js";

	const { tenant }: SolutionsProps = $props();
	const services = getSolutionsServices(tenant);
</script>

<Layout withStructuredData>
	<Section background={Background.LightGradient} centered gridHeadline="container-6" gridDesc="container-6" hero>
		{#snippet headline()}
			<Formatted text="cg[[IT řešení]] pro malé i velké firmy" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="sp[[Nejsme jen webaři.]] Vyřešíme za vás CRM, podnikový systém i automatizaci nebo digitalizaci na míru."
				/>
			</p>
		{/snippet}
		{#snippet cta()}
			<Link
				variant={ButtonVariant.PhloxOutlineLightBg}
				class="flex items-center justify-center mx-auto mt-2"
				href={getConnectWithFastestPath(tenant)}
			>
				<Formatted text="Poptat řešení na míru" />
				<Icon icon={arrowNext} class="ml-2.5 text-fstgrey-900 w-5"></Icon>
			</Link>
		{/snippet}
	</Section>

	<Section
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
		paddingFollowNext
	>
		{#snippet headline()}
			<Formatted text="Baví nás cg[[zefektivňovat]] fungování firem" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="Aby firmy zlepšily obchodní procesy, řízení dat nebo zefektivnily práci zaměstnanců, sp[[potřebují IT řešení na míru]] – od přizpůsobeného CRM přes integraci různých systémů (např. platební brána) až po automatizaci rutinních činností."
				/>
			</p>
			<p>
				<Formatted
					text="sp[[Proto ve Fastest jdeme za hranice vývoje [{getWebAppsPath(
						tenant,
					)}][[webových]]]] sp[[ a mobilních aplikací.]] Máme přesah do narovnávání firemních procesů a dodáváme IT řešení přesně podle potřeby klientů. sp[[Nespoléháme na krabicová řešení,]] ale s každým zákazníkem absolvujeme podrobnou konzultaci, kde zjišťujeme jeho problémy a požadavky."
				/>
			</p>
		{/snippet}
	</Section>

	<Section centered gridHeadline="container-6" noPadding="bot">
		{#snippet headline()}
			<Formatted text="Která cg[[IT řešení]] pro vás zajistíme?" />
		{/snippet}
	</Section>
	<ServicesCards cards={services} />

	<Section centered gridHeadline="container-6">
		{#snippet headline()}
			<Formatted text="Časté otázky k cg[[IT řešení]]" />
		{/snippet}
		<FaqItems faqs={getFaqsSolutions(tenant)} />
	</Section>

	<Section
		background={Background.Onyx}
		gridHeadline="container-6 lg:container-start-1 lg:container-end-5 xl:container-start-2 xl:container-end-4 max-lg:text-center"
		gridDesc="lg:container-start-7 lg:container-end-12 xl:container-start-6 xl:container-end-11"
	>
		{#snippet headline()}
			<Formatted text="cg[[Proč]] si plácnout s cg[[Fastest Solution]]" />
		{/snippet}
		{#snippet desc()}
			<FoldingCards color={Background.Onyx} cards={getReasonsForCoop(tenant)} />
		{/snippet}
		<div class="container-12">
			<Link
				variant={ButtonVariant.Phlox}
				class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
				href={getConnectWithFastestPath(tenant)}
				>Chci fastest služby
				<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
			</Link>
		</div>
	</Section>

	{@const contacts = getBookMeetingPeople(tenant)}
	<BookMeeting contacts={[contacts[BookMeetingPerson.Kucera], contacts[BookMeetingPerson.Svejda]]} />
</Layout>
